import { roomList } from 'config/Constants';
import { useInterval } from 'hooks/usePolling';
import { useStore } from 'hooks/useStore';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { getJitteredInterval } from 'utils/pollingUtility';
import './jobTagScreen.module.scss';

export const useJobTagsScreen = () => {
  const { JobTagsStore } = useStore();
  const contentRef = useRef<HTMLDivElement>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  useEffect(() => {
    JobTagsStore.fetAllJobs();
  }, []);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await JobTagsStore.pollData();
    } catch (error) {
      if (environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for job tags');
    }
  }, POLLING_INTERVAL);

  // Intercept Ctrl+P / Cmd+P to use custom print function
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+P (Windows/Linux) or Cmd+P (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault(); // Prevent default browser print dialog
        handlePrint();
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleDropdownOptionChange = async (value: string) => {
    JobTagsStore.setSelectedJobId(value);
    try {
      // Use the regular fetch method for immediate response with loading indicator
      await JobTagsStore.fetchJobDetails(value);
    } catch (error) {
      console.log('Error occured in handleDropdownOptionChange=> ', error);
    }
  };

  const getDate = () => {
    const date = new Date();
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}-${year}`;
  };

  const getTime = () => {
    const date = new Date();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${hours}:${minutesStr} ${ampm}`;
  };

  const getRoomcolor = (room: string) => {
    for (const item of roomList) {
      if (item.label === room) {
        return item.color;
      }
    }
    return 'black';
  };

  const handlePrint = () => {
    const printContents = document.getElementById('content');

    if (!printContents) {
      alert('No element with id="print" found!');
      return;
    }

    // 1. Collect all class names used in the print div
    const classSet = new Set<string>();
    printContents.querySelectorAll('*').forEach(el => {
      if (typeof el.className === 'string') {
        el.className.split(' ').forEach(cls => classSet.add(cls));
      }
    });
    if (typeof printContents.className === 'string') {
      printContents.className.split(' ').forEach(cls => classSet.add(cls));
    }

    // 2. Extract CSS rules for those class names
    let extractedCSS = '';
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules || [])) {
          if ((rule as CSSStyleRule).selectorText !== undefined) {
            const selector = (rule as CSSStyleRule).selectorText;
            if (
              selector &&
              Array.from(classSet).some(cls =>
                selector.split(',').some(sel => sel.includes(`.${cls}`))
              )
            ) {
              extractedCSS += rule.cssText + '\\n';
            }
          }
        }
      } catch (e) {
        // Some stylesheets may be cross-origin and not accessible
        continue;
      }
    }

    // 3. Copy <base> tag for relative URLs (images, fonts, etc.)
    const base = document.querySelector('base');
    const baseTag = base ? base.outerHTML : '';

    // 4. Copy all <link rel="preconnect"> and <link rel="preload"> for fonts
    const fontLinks = '';
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach(node => {
      extractedCSS += node.outerHTML;
    });

    // 5. Open print window and inject content and styles
    const clone = printContents.cloneNode(true) as HTMLElement;
    const printWindow = window.open('', '', 'width=900,height=650');
    if (!printWindow) {
      alert('Could not open print window!');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          ${baseTag}
          <title>Print</title>
          ${fontLinks}
          <style>
            @page { size: landscape; margin: 10mm; }
            body { 
              background: white !important; 
              margin: 0;
              padding: 0;
              @media print {
                /* General font enhancements for print */
                * {
                  font-weight: bold !important;
                  box-sizing: border-box;
                }

                #content {
                  background-color: #ffffff;
                  width: 100%;
                  height: 100%;
                  padding: 1rem;
                  display: flex;
                  flex-direction: column;
                }

                #headerContainer {
                  display: flex;
                  flex-direction: row;
                  justify-content: space-between;
                  font-size: 1.2rem !important;
                  margin-bottom: 0.5rem;
                }

                #headerContainer p {
                  font-size: 1.2rem !important;
                  font-weight: bold !important;
                  margin: 0;
                }

                .timestamp {
                  display: inline !important;
                  margin-left: 1rem;
                }

                #jobDetailContainer {
                  /* No direct styles - keep vertical layout */
                }

                #jobIdContainer {
                  display: flex;
                  justify-content: center;
                  flex-direction: column;
                  width: 100%;
                }

                #jobIdContainer p:first-child {
                  font-size: 90px !important;
                  font-weight: bold !important;
                  margin: 0;
                  line-height: 1;
                }

                #jobIdContainer p:last-child {
                  font-size: 50px !important;
                  font-weight: bold !important;
                  margin: 0.5rem 0 0 0;
                  line-height: 1;
                }

                #job-details {
                  margin-top: 40px;
                  color: #5a5959;
                }

                #jobDetailsRow {
                  display: flex;
                  align-items: center;
                  height: 3.5rem !important;
                  min-height: 3.5rem !important;
                }

                #jobDetailKey {
                  padding-left: 1.5rem;
                  font-weight: bold !important;
                  font-size: 1.4rem !important;
                  white-space: nowrap;
                }

                #jobDetailValue {
                  font-weight: bold !important;
                  font-size: 1.4rem !important;
                  word-break: break-word;
                }

                #noJobSelectedContainer {
                  height: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 35px !important;
                  font-weight: bold !important;
                  color: #4d2e6d;
                }
                
                #print-btn { display: none !important; }
                .timestamp-hidden { display: none !important; }
              } 
            }
            ${extractedCSS}
          </style>
        </head>
        <body>
          ${clone.outerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    printWindow.onload = function () {
      setTimeout(function () {
        printWindow.print();
        printWindow.close();
      }, 700);
    };
  };

  return {
    isAllJobsLoading: JobTagsStore.isAllJobsLoading,
    allJobs: JobTagsStore.allJobs,
    selectedJobId: JobTagsStore.selectedJobId,
    isJobDetailsLoading: JobTagsStore.isJobDetailsLoading,
    jobDetails: JobTagsStore.jobDetails,
    contentRef: contentRef,
    handleDropdownOptionChange: handleDropdownOptionChange,
    getDate: getDate,
    getTime: getTime,
    getRoomcolor: getRoomcolor,
    handlePrint: handlePrint
  };
};
